import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, useAuth } from '@ddc-gds/sax-hub-core';
import { 
  Box, 
  Button, 
  Container, 
  TextField, 
  Typography, 
  Paper, 
  Alert,
  CircularProgress,
  Link
} from '@mui/material';

const AUTH_ERROR_STORAGE_KEY = 'sax-auth-error';
const AUTH_ERROR_EVENT = 'sax-auth-error';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resetSent, setResetSent] = useState(false);
  
  const { user, role } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const consumeAuthError = () => {
      const authError = sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);
      if (authError) {
        setErrorMsg(authError);
        sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
      }
    };

    consumeAuthError();
    window.addEventListener(AUTH_ERROR_EVENT, consumeAuthError);

    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, consumeAuthError);
    };
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, role, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setErrorMsg(error.message);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Ocurrió un error inesperado al iniciar sesión.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setErrorMsg('Por favor ingresa tu correo electrónico para restablecer la contraseña.');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    setResetSent(false);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password', // Default reset page
      });

      if (error) {
        setErrorMsg(error.message);
      } else {
        setResetSent(true);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Ocurrió un error inesperado al solicitar el restablecimiento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        
        {/* Usando SVG en lugar de Image para evitar dependencias faltantes */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.main', borderRadius: '50%', color: 'white' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
        </Box>

        <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
          SAX Hub
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {resetSent && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            Se ha enviado un correo con instrucciones para restablecer tu contraseña.
          </Alert>
        )}

        <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Correo Electrónico"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Contraseña"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                handlePasswordReset();
              }}
              disabled={loading}
              sx={{ textDecoration: 'none' }}
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};
